import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { 
  Users, 
  Home, 
  MessageSquare,
  BarChart3,
  CheckCircle,
  XCircle,
  DollarSign,
  TrendingUp,
  Sparkles,
  Image,
  FileText,
  Activity,
  Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  Area,
  AreaChart
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = ['#8b5cf6', '#10b981', '#f59e0b', '#ef4444'];

const Admin = () => {
  const { getAuthHeaders } = useAuth();
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [users, setUsers] = useState([]);
  const [listings, setListings] = useState([]);
  const [supportMessages, setSupportMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // User selection state for bulk delete
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [deletingUsers, setDeletingUsers] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, analyticsRes, usersRes, listingsRes, supportRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers: getAuthHeaders() }),
        axios.get(`${API}/admin/analytics`, { headers: getAuthHeaders() }),
        axios.get(`${API}/admin/users`, { headers: getAuthHeaders() }),
        axios.get(`${API}/admin/listings`, { headers: getAuthHeaders() }),
        axios.get(`${API}/admin/support`, { headers: getAuthHeaders() })
      ]);
      setStats(statsRes.data);
      setAnalytics(analyticsRes.data);
      setUsers(usersRes.data);
      setListings(listingsRes.data);
      setSupportMessages(supportRes.data);
    } catch (error) {
      console.error('Admin fetch error:', error);
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseTicket = async (messageId) => {
    try {
      await axios.put(
        `${API}/admin/support/${messageId}/status?status=closed`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success('Ticket closed');
      fetchData();
    } catch (error) {
      toast.error('Failed to close ticket');
    }
  };

  const handleFixListingLimits = async () => {
    toast.info('Fixing listing limits...');
    try {
      const response = await axios.post(
        `${API}/admin/fix-listing-limits`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success(`Fixed! Pro users: ${response.data.pro_users_fixed}, Negative values: ${response.data.negative_fixed}`);
      fetchData(); // Refresh data
    } catch (error) {
      console.error('Fix listing limits error:', error);
      toast.error('Failed to fix listing limits');
    }
  };

  const handleUpgradeToPro = async (userId, userName) => {
    toast.info(`Upgrading ${userName}...`);
    
    try {
      const response = await axios.post(
        `${API}/admin/users/${userId}/upgrade-pro`,
        {},
        { headers: getAuthHeaders() }
      );
      console.log('Upgrade response:', response.data);
      toast.success(`${userName} upgraded to Pro!`);
      fetchData();
    } catch (error) {
      console.error('Upgrade error:', error.response?.data || error);
      toast.error(error.response?.data?.detail || 'Failed to upgrade user');
    }
  };

  const handleResetTrial = async (userId, userName) => {
    if (!confirm(`Reset trial for ${userName}?`)) return;
    try {
      await axios.post(
        `${API}/admin/users/${userId}/reset-trial`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success('Trial reset successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to reset trial');
    }
  };

  // User selection handlers for bulk delete
  const handleSelectUser = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSelectAllUsers = () => {
    // Filter out admin users when selecting all
    const nonAdminUsers = users.filter(u => 
      !u.is_admin && u.email !== 'erick@listingpro-ai.com'
    );
    if (selectedUsers.length === nonAdminUsers.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(nonAdminUsers.map(u => u.id));
    }
  };

  const handleBulkDeleteUsers = async () => {
    if (selectedUsers.length === 0) {
      toast.error('No users selected');
      return;
    }
    
    const confirmMsg = `Are you sure you want to delete ${selectedUsers.length} user(s)? This will also delete all their listings, photos, contacts, and data. This action cannot be undone.`;
    if (!confirm(confirmMsg)) return;
    
    setDeletingUsers(true);
    try {
      const response = await axios.post(
        `${API}/admin/users/bulk-delete`,
        { user_ids: selectedUsers },
        { headers: getAuthHeaders() }
      );
      
      const { deleted_count, skipped } = response.data;
      
      if (deleted_count > 0) {
        toast.success(`Deleted ${deleted_count} user(s)!`);
      }
      if (skipped?.length > 0) {
        toast.warning(`Skipped ${skipped.length} user(s): ${skipped.map(s => s.reason).join(', ')}`);
      }
      
      setSelectedUsers([]);
      fetchData(); // Refresh the user list
    } catch (error) {
      console.error('Bulk delete error:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete users');
    } finally {
      setDeletingUsers(false);
    }
  };

  // S3 Migration State
  const [migrationStatus, setMigrationStatus] = useState(null);
  const [migrationLoading, setMigrationLoading] = useState(false);
  const [pollingMigration, setPollingMigration] = useState(false);

  // Subscription Management State
  const [proUsers, setProUsers] = useState([]);
  const [proUsersLoading, setProUsersLoading] = useState(false);
  const [syncingUserId, setSyncingUserId] = useState(null);

  const fetchProUsers = async () => {
    setProUsersLoading(true);
    try {
      const response = await axios.get(
        `${API}/admin/pro-users`,
        { headers: getAuthHeaders() }
      );
      setProUsers(response.data.users || []);
    } catch (error) {
      console.error('Error fetching pro users:', error);
      toast.error('Failed to fetch Pro users');
    } finally {
      setProUsersLoading(false);
    }
  };

  const handleSyncStripeIds = async (userId, userName) => {
    setSyncingUserId(userId);
    try {
      const response = await axios.post(
        `${API}/admin/users/${userId}/sync-stripe-ids`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success(`Stripe IDs synced for ${userName}`);
      fetchProUsers(); // Refresh the list
    } catch (error) {
      console.error('Sync error:', error);
      toast.error(error.response?.data?.detail || 'Failed to sync Stripe IDs');
    } finally {
      setSyncingUserId(null);
    }
  };

  const handleDowngradeUser = async (userId, userName) => {
    if (!confirm(`Downgrade ${userName} from Pro to Trial? This will remove their Pro subscription.`)) return;
    
    try {
      await axios.post(
        `${API}/admin/users/${userId}/downgrade-to-trial`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success(`${userName} downgraded to Trial`);
      fetchProUsers();
      fetchData(); // Refresh main stats
    } catch (error) {
      console.error('Downgrade error:', error);
      toast.error(error.response?.data?.detail || 'Failed to downgrade user');
    }
  };

  const handleStartMigration = async () => {
    if (!confirm('Start migrating photos to S3? This may take several minutes.')) return;
    
    setMigrationLoading(true);
    try {
      const response = await axios.post(
        `${API}/admin/migrate-photos-to-s3`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success(response.data.message);
      setMigrationStatus(response.data);
      
      // Start polling for status
      if (response.data.status === 'started') {
        setPollingMigration(true);
        pollMigrationStatus();
      }
    } catch (error) {
      console.error('Migration error:', error);
      toast.error(error.response?.data?.detail || 'Failed to start migration');
    } finally {
      setMigrationLoading(false);
    }
  };

  const pollMigrationStatus = async () => {
    try {
      const response = await axios.get(
        `${API}/admin/migration-status`,
        { headers: getAuthHeaders() }
      );
      setMigrationStatus(response.data);
      
      if (response.data.in_progress) {
        // Continue polling every 2 seconds
        setTimeout(pollMigrationStatus, 2000);
      } else {
        setPollingMigration(false);
        if (response.data.completed_at) {
          toast.success(`Migration complete! ${response.data.migrated} photos migrated.`);
        }
      }
    } catch (error) {
      console.error('Status poll error:', error);
      setPollingMigration(false);
    }
  };

  const fetchMigrationStatus = async () => {
    try {
      const response = await axios.get(
        `${API}/admin/migration-status`,
        { headers: getAuthHeaders() }
      );
      setMigrationStatus(response.data);
      
      // If migration is in progress, start polling
      if (response.data.in_progress && !pollingMigration) {
        setPollingMigration(true);
        pollMigrationStatus();
      }
    } catch (error) {
      console.error('Fetch migration status error:', error);
    }
  };

  // Fetch migration status on load
  useEffect(() => {
    fetchMigrationStatus();
  }, []);

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  };

  // Prepare subscription pie chart data
  const subscriptionPieData = analytics ? [
    { name: 'Active Pro', value: analytics.subscriptions.active, color: '#10b981' },
    { name: 'Trial', value: analytics.subscriptions.trial, color: '#8b5cf6' },
    { name: 'Cancelled', value: analytics.subscriptions.cancelled, color: '#ef4444' },
    { name: 'Expired', value: analytics.subscriptions.expired_trials, color: '#6b7280' }
  ].filter(item => item.value > 0) : [];

  if (loading) {
    return (
      <div className="min-h-screen bg-secondary/30">
        <Navbar variant="app" />
        <div className="flex items-center justify-center h-[60vh]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-secondary/30" data-testid="admin-panel">
      <Navbar variant="app" />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-heading font-bold text-foreground">Admin Dashboard</h1>
          <Button 
            onClick={handleFixListingLimits}
            variant="outline"
            className="bg-orange-500 hover:bg-orange-600 text-white border-orange-500"
          >
            Fix Listing Limits
          </Button>
        </div>

        {/* Revenue & Key Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="border-border/40 bg-gradient-to-br from-green-500/10 to-green-600/5">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-500/20 rounded-lg flex items-center justify-center">
                  <DollarSign className="text-green-500" size={20} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-green-600">{formatCurrency(analytics?.revenue?.current_mrr || 0)}</p>
                  <p className="text-sm text-muted-foreground">Monthly Revenue</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-border/40">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Users className="text-primary" size={20} />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats?.total_users || 0}</p>
                  <p className="text-sm text-muted-foreground">Total Users</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-border/40">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <TrendingUp className="text-blue-600" size={20} />
                </div>
                <div>
                  <p className="text-2xl font-bold">{analytics?.subscriptions?.active || 0}</p>
                  <p className="text-sm text-muted-foreground">Paid Subscribers</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-border/40">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <Activity className="text-amber-600" size={20} />
                </div>
                <div>
                  <p className="text-2xl font-bold">{analytics?.subscriptions?.conversion_rate || 0}%</p>
                  <p className="text-sm text-muted-foreground">Conversion Rate</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Traffic Analytics Section */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="border-border/40 bg-gradient-to-br from-blue-500/10 to-blue-600/5">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-500/20 rounded-lg flex items-center justify-center">
                  <BarChart3 className="text-blue-500" size={20} />
                </div>
                <div>
                  <p className="text-2xl font-bold text-blue-600">{analytics?.traffic?.pageviews_today || 0}</p>
                  <p className="text-sm text-muted-foreground">Page Views Today</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-border/40">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Users className="text-purple-600" size={20} />
                </div>
                <div>
                  <p className="text-2xl font-bold">{analytics?.traffic?.sessions_today || 0}</p>
                  <p className="text-sm text-muted-foreground">Sessions Today</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-border/40">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <TrendingUp className="text-green-600" size={20} />
                </div>
                <div>
                  <p className="text-2xl font-bold">{analytics?.traffic?.sessions_7d || 0}</p>
                  <p className="text-sm text-muted-foreground">Sessions (7 Days)</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-border/40">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <Activity className="text-amber-600" size={20} />
                </div>
                <div>
                  <p className="text-2xl font-bold">{analytics?.traffic?.pageviews_7d || 0}</p>
                  <p className="text-sm text-muted-foreground">Page Views (7 Days)</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Traffic Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Traffic by Day */}
          <Card className="border-border/40">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <BarChart3 size={18} className="text-blue-500" />
                Traffic (30 Days)
              </CardTitle>
              <CardDescription>Page views and sessions over time</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={analytics?.traffic?.traffic_by_day || []}>
                    <defs>
                      <linearGradient id="pvGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                      </linearGradient>
                      <linearGradient id="sessGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} stroke="#888" interval={4} />
                    <YAxis tick={{ fontSize: 12 }} stroke="#888" />
                    <Tooltip contentStyle={{ backgroundColor: '#1a1a2e', border: '1px solid #333' }} />
                    <Legend />
                    <Area type="monotone" dataKey="pageviews" name="Page Views" stroke="#3b82f6" strokeWidth={2} fill="url(#pvGradient)" />
                    <Area type="monotone" dataKey="sessions" name="Sessions" stroke="#8b5cf6" strokeWidth={2} fill="url(#sessGradient)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Conversion Funnel */}
          <Card className="border-border/40">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <TrendingUp size={18} className="text-green-500" />
                Conversion Funnel (7 Days)
              </CardTitle>
              <CardDescription>From visitor to Pro subscriber</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4 py-2">
                {/* Funnel visualization - labels outside bars */}
                <div className="space-y-4">
                  {/* Visitors */}
                  <div>
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-sm font-medium text-gray-700">Visitors</span>
                      <span className="text-sm font-semibold text-blue-600">{analytics?.funnel?.visitors || 0}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-6">
                      <div className="bg-blue-500 h-full rounded-full" style={{ width: '100%' }}></div>
                    </div>
                  </div>
                  
                  {/* Signups */}
                  <div>
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-sm font-medium text-gray-700">Signups</span>
                      <span className="text-sm font-semibold text-purple-600">
                        {analytics?.funnel?.signups || 0} ({analytics?.funnel?.visitor_to_signup_rate || 0}%)
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-6">
                      <div 
                        className="bg-purple-500 h-full rounded-full transition-all duration-300" 
                        style={{ width: `${Math.max(2, Math.min(100, analytics?.funnel?.visitor_to_signup_rate || 0))}%` }}
                      ></div>
                    </div>
                  </div>
                  
                  {/* Created Listing */}
                  <div>
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-sm font-medium text-gray-700">Created Listing</span>
                      <span className="text-sm font-semibold text-green-600">
                        {analytics?.funnel?.created_listing || 0} ({analytics?.funnel?.signup_to_listing_rate || 0}%)
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-6">
                      <div 
                        className="bg-green-500 h-full rounded-full transition-all duration-300" 
                        style={{ width: `${Math.max(2, Math.min(100, (analytics?.funnel?.created_listing / Math.max(1, analytics?.funnel?.visitors)) * 100 || 0))}%` }}
                      ></div>
                    </div>
                  </div>
                  
                  {/* Pro Upgrade */}
                  <div>
                    <div className="flex justify-between items-center mb-1">
                      <span className="text-sm font-medium text-gray-700">Pro Upgrade</span>
                      <span className="text-sm font-semibold text-amber-600">
                        {analytics?.funnel?.upgraded_pro || 0} ({analytics?.funnel?.listing_to_pro_rate || 0}%)
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-6">
                      <div 
                        className="bg-amber-500 h-full rounded-full transition-all duration-300" 
                        style={{ width: `${Math.max(2, Math.min(100, (analytics?.funnel?.upgraded_pro / Math.max(1, analytics?.funnel?.visitors)) * 100 || 0))}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Traffic Sources & Top Pages */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Traffic Sources */}
          <Card className="border-border/40">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Activity size={18} className="text-purple-500" />
                Traffic Sources (7 Days)
              </CardTitle>
              <CardDescription>Where your visitors come from</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {(analytics?.traffic?.traffic_sources || []).length === 0 ? (
                  <p className="text-muted-foreground text-sm text-center py-4">No traffic data yet. Data will appear as users visit your site.</p>
                ) : (
                  analytics?.traffic?.traffic_sources?.map((source, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-muted/30 rounded-lg">
                      <div>
                        <p className="font-medium text-sm">
                          {source.source === 'direct' ? 'Direct Traffic' : source.source}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {source.medium !== 'none' && source.medium} {source.campaign !== 'none' && `/ ${source.campaign}`}
                        </p>
                      </div>
                      <Badge variant="secondary">{source.sessions} sessions</Badge>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>

          {/* Top Pages */}
          <Card className="border-border/40">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText size={18} className="text-blue-500" />
                Top Pages (7 Days)
              </CardTitle>
              <CardDescription>Most visited pages</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {(analytics?.traffic?.top_pages || []).length === 0 ? (
                  <p className="text-muted-foreground text-sm text-center py-4">No page view data yet.</p>
                ) : (
                  analytics?.traffic?.top_pages?.map((page, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-muted/30 rounded-lg">
                      <p className="font-medium text-sm truncate max-w-[200px]">{page.page}</p>
                      <Badge variant="outline">{page.views} views</Badge>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Revenue History Chart */}
          <Card className="border-border/40">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <DollarSign size={18} className="text-green-500" />
                Revenue History
              </CardTitle>
              <CardDescription>Monthly subscription revenue</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={analytics?.revenue?.history || []}>
                    <defs>
                      <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                    <XAxis dataKey="month" tick={{ fontSize: 12 }} stroke="#888" />
                    <YAxis tick={{ fontSize: 12 }} stroke="#888" tickFormatter={(v) => `$${v}`} />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#1a1a2e', border: '1px solid #333' }}
                      formatter={(value) => [`$${value.toFixed(2)}`, 'Revenue']}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="revenue" 
                      stroke="#10b981" 
                      strokeWidth={2}
                      fill="url(#revenueGradient)" 
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Subscription Breakdown */}
          <Card className="border-border/40">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <BarChart3 size={18} className="text-primary" />
                Subscription Breakdown
              </CardTitle>
              <CardDescription>Current subscriber distribution</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-64 flex items-center">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={subscriptionPieData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={90}
                      paddingAngle={2}
                      dataKey="value"
                      label={({ name, value }) => `${name}: ${value}`}
                    >
                      {subscriptionPieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#1a1a2e', border: '1px solid #333' }}
                    />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* User Growth & Activity Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* User Signups */}
          <Card className="border-border/40">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Users size={18} className="text-primary" />
                User Signups (30 Days)
              </CardTitle>
              <CardDescription>{analytics?.recent_activity?.signups_7d || 0} new users in last 7 days</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={analytics?.user_growth || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                    <XAxis 
                      dataKey="date" 
                      tick={{ fontSize: 10 }} 
                      stroke="#888"
                      interval={4}
                    />
                    <YAxis tick={{ fontSize: 12 }} stroke="#888" />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#1a1a2e', border: '1px solid #333' }}
                    />
                    <Bar dataKey="signups" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Listings Created */}
          <Card className="border-border/40">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Home size={18} className="text-green-500" />
                Listings Created (30 Days)
              </CardTitle>
              <CardDescription>{analytics?.recent_activity?.listings_7d || 0} new listings in last 7 days</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-48">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={analytics?.listing_activity || []}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                    <XAxis 
                      dataKey="date" 
                      tick={{ fontSize: 10 }} 
                      stroke="#888"
                      interval={4}
                    />
                    <YAxis tick={{ fontSize: 12 }} stroke="#888" />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#1a1a2e', border: '1px solid #333' }}
                    />
                    <Bar dataKey="listings" fill="#10b981" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* AI Usage Stats */}
        <Card className="border-border/40 mb-8">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Sparkles size={18} className="text-amber-500" />
              AI Feature Usage
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-secondary/50 rounded-lg p-4 text-center">
                <Image className="mx-auto mb-2 text-blue-500" size={24} />
                <p className="text-2xl font-bold">{analytics?.ai_usage?.total_photos || 0}</p>
                <p className="text-sm text-muted-foreground">Total Photos</p>
              </div>
              <div className="bg-secondary/50 rounded-lg p-4 text-center">
                <Sparkles className="mx-auto mb-2 text-purple-500" size={24} />
                <p className="text-2xl font-bold">{analytics?.ai_usage?.enhanced_photos || 0}</p>
                <p className="text-sm text-muted-foreground">AI Enhanced</p>
              </div>
              <div className="bg-secondary/50 rounded-lg p-4 text-center">
                <Home className="mx-auto mb-2 text-green-500" size={24} />
                <p className="text-2xl font-bold">{analytics?.ai_usage?.staged_photos || 0}</p>
                <p className="text-sm text-muted-foreground">Virtual Staged</p>
              </div>
              <div className="bg-secondary/50 rounded-lg p-4 text-center">
                <FileText className="mx-auto mb-2 text-amber-500" size={24} />
                <p className="text-2xl font-bold">{analytics?.ai_usage?.content_generated || 0}</p>
                <p className="text-sm text-muted-foreground">Content Generated</p>
              </div>
            </div>
            <div className="mt-4 flex items-center justify-center">
              <div className="bg-primary/10 rounded-full px-4 py-2">
                <span className="text-sm text-muted-foreground">Enhancement Rate: </span>
                <span className="font-bold text-primary">{analytics?.ai_usage?.enhancement_rate || 0}%</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabs for Users, Listings, Support */}
        <Card className="border-border/40">
          <Tabs defaultValue="users">
            <CardHeader>
              <TabsList>
                <TabsTrigger value="users" data-testid="admin-tab-users">Users ({users.length})</TabsTrigger>
                <TabsTrigger value="listings" data-testid="admin-tab-listings">Listings ({listings.length})</TabsTrigger>
                <TabsTrigger value="support" data-testid="admin-tab-support">Support ({supportMessages.filter(m => m.status === 'open').length})</TabsTrigger>
                <TabsTrigger value="migration" data-testid="admin-tab-migration">S3 Migration</TabsTrigger>
                <TabsTrigger value="subscriptions" data-testid="admin-tab-subscriptions">Subscriptions</TabsTrigger>
              </TabsList>
            </CardHeader>
            <CardContent>
              {/* Users Tab */}
              <TabsContent value="users">
                {/* Bulk Delete Controls */}
                {selectedUsers.length > 0 && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center justify-between">
                    <span className="text-red-800 font-medium">
                      {selectedUsers.length} user(s) selected
                    </span>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={handleBulkDeleteUsers}
                      disabled={deletingUsers}
                      className="gap-2"
                      data-testid="bulk-delete-btn"
                    >
                      <Trash2 size={16} />
                      {deletingUsers ? 'Deleting...' : `Delete ${selectedUsers.length} User(s)`}
                    </Button>
                  </div>
                )}
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground w-12">
                          <Checkbox
                            checked={selectedUsers.length > 0 && selectedUsers.length === users.filter(u => !u.is_admin && u.email !== 'erick@listingpro-ai.com').length}
                            onCheckedChange={handleSelectAllUsers}
                            data-testid="select-all-users"
                          />
                        </th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">User</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Plan</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Status</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Joined</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {users.map((user) => {
                        const isProtected = user.is_admin || user.email === 'erick@listingpro-ai.com';
                        return (
                          <tr key={user.id} className={`border-b border-border/50 hover:bg-secondary/30 ${selectedUsers.includes(user.id) ? 'bg-red-50' : ''}`}>
                            <td className="py-3 px-4">
                              {!isProtected ? (
                                <Checkbox
                                  checked={selectedUsers.includes(user.id)}
                                  onCheckedChange={() => handleSelectUser(user.id)}
                                  data-testid={`select-user-${user.id}`}
                                />
                              ) : (
                                <span className="text-xs text-muted-foreground">🔒</span>
                              )}
                            </td>
                            <td className="py-3 px-4">
                              <div>
                                <p className="font-medium">{user.name}</p>
                                <p className="text-sm text-muted-foreground">{user.email}</p>
                              </div>
                            </td>
                            <td className="py-3 px-4">
                              <Badge variant={user.plan === 'Pro' ? 'default' : 'outline'} 
                                     className={user.plan === 'Pro' ? 'bg-green-600' : ''}>
                                {user.plan}
                              </Badge>
                            </td>
                            <td className="py-3 px-4">
                              <Badge className={
                                user.subscription_status === 'active' ? 'bg-green-100 text-green-700' : 
                                user.subscription_status === 'trial_expired' ? 'bg-red-100 text-red-700' :
                                'bg-gray-100 text-gray-700'
                              }>
                                {user.subscription_status}
                              </Badge>
                            </td>
                            <td className="py-3 px-4 text-sm text-muted-foreground">
                              {formatDate(user.created_at)}
                            </td>
                            <td className="py-3 px-4">
                              <div className="flex gap-2">
                                {user.plan === 'Trial' && (
                                  <>
                                    <Button 
                                      variant="outline" 
                                      size="sm"
                                      onClick={() => handleResetTrial(user.id, user.name)}
                                      data-testid={`reset-trial-${user.id}`}
                                    >
                                      Reset Trial
                                    </Button>
                                    <Button 
                                      variant="default" 
                                      size="sm"
                                      className="bg-green-600 hover:bg-green-700"
                                      onClick={() => handleUpgradeToPro(user.id, user.name)}
                                      data-testid={`upgrade-pro-${user.id}`}
                                    >
                                      Upgrade Pro
                                    </Button>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </TabsContent>

              {/* Listings Tab */}
              <TabsContent value="listings">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Listing</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Price</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Status</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Created</th>
                      </tr>
                    </thead>
                    <tbody>
                      {listings.map((listing) => (
                        <tr key={listing.id} className="border-b border-border/50 hover:bg-secondary/30">
                          <td className="py-3 px-4">
                            <p className="font-medium truncate max-w-xs">{listing.title}</p>
                          </td>
                          <td className="py-3 px-4 text-sm">
                            {listing.price ? `$${listing.price.toLocaleString()}` : 'N/A'}
                          </td>
                          <td className="py-3 px-4">
                            <Badge className={
                              listing.status === 'Active' ? 'bg-green-100 text-green-700' :
                              listing.status === 'Pending' ? 'bg-yellow-100 text-yellow-700' :
                              'bg-gray-100 text-gray-700'
                            }>
                              {listing.status}
                            </Badge>
                          </td>
                          <td className="py-3 px-4 text-sm text-muted-foreground">
                            {formatDate(listing.created_at)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </TabsContent>

              {/* Support Tab */}
              <TabsContent value="support">
                {supportMessages.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <MessageSquare className="mx-auto mb-2" size={32} />
                    <p>No support messages</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {supportMessages.map((message) => (
                      <div key={message.id} className="border border-border/50 rounded-lg p-4 hover:bg-secondary/30">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <p className="font-medium">{message.user_name}</p>
                            <p className="text-sm text-muted-foreground">{message.user_email}</p>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge className={message.status === 'open' ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}>
                              {message.status}
                            </Badge>
                            {message.status === 'open' && (
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => handleCloseTicket(message.id)}
                              >
                                <CheckCircle size={16} className="text-green-600" />
                              </Button>
                            )}
                          </div>
                        </div>
                        <p className="text-sm mb-2">{message.message}</p>
                        <p className="text-xs text-muted-foreground">{formatDate(message.created_at)}</p>
                      </div>
                    ))}
                  </div>
                )}
              </TabsContent>

              {/* S3 Migration Tab */}
              <TabsContent value="migration">
                <div className="space-y-6">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <h3 className="font-semibold text-blue-800 mb-2">Photo Migration to S3</h3>
                    <p className="text-sm text-blue-700">
                      This tool migrates photos from base64 format (stored in database) to AWS S3 storage. 
                      Photos will be converted to WebP format for better compression.
                    </p>
                  </div>

                  {/* Current Status */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-secondary/30 rounded-lg p-4 text-center">
                      <p className="text-2xl font-bold text-orange-600">{migrationStatus?.remaining_base64 || 0}</p>
                      <p className="text-sm text-muted-foreground">Base64 Photos</p>
                    </div>
                    <div className="bg-secondary/30 rounded-lg p-4 text-center">
                      <p className="text-2xl font-bold text-green-600">{migrationStatus?.total_s3 || 0}</p>
                      <p className="text-sm text-muted-foreground">S3 Photos</p>
                    </div>
                    <div className="bg-secondary/30 rounded-lg p-4 text-center">
                      <p className="text-2xl font-bold text-blue-600">{migrationStatus?.migrated || 0}</p>
                      <p className="text-sm text-muted-foreground">Migrated This Run</p>
                    </div>
                    <div className="bg-secondary/30 rounded-lg p-4 text-center">
                      <p className="text-2xl font-bold text-red-600">{migrationStatus?.errors || 0}</p>
                      <p className="text-sm text-muted-foreground">Errors</p>
                    </div>
                  </div>

                  {/* Progress Bar (when in progress) */}
                  {migrationStatus?.in_progress && (
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span>Migration Progress</span>
                        <span>{migrationStatus.progress_percent}%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-4">
                        <div 
                          className="bg-blue-600 h-4 rounded-full transition-all duration-500"
                          style={{ width: `${migrationStatus.progress_percent || 0}%` }}
                        />
                      </div>
                      <p className="text-sm text-muted-foreground text-center">
                        Processing batch {migrationStatus.current_batch}... {migrationStatus.migrated} of {migrationStatus.total_photos} photos
                      </p>
                    </div>
                  )}

                  {/* Completed Message */}
                  {migrationStatus?.completed_at && !migrationStatus?.in_progress && (
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                      <p className="text-green-800">
                        ✅ Migration completed at {new Date(migrationStatus.completed_at).toLocaleString()}
                      </p>
                      <p className="text-sm text-green-700">
                        {migrationStatus.migrated} photos migrated, {migrationStatus.errors} errors
                      </p>
                    </div>
                  )}

                  {/* Action Button */}
                  <div className="flex gap-4">
                    <Button 
                      onClick={handleStartMigration}
                      disabled={migrationLoading || migrationStatus?.in_progress}
                      className="bg-blue-600 hover:bg-blue-700"
                      data-testid="start-migration-btn"
                    >
                      {migrationStatus?.in_progress ? (
                        <>
                          <span className="animate-spin mr-2">⏳</span>
                          Migration In Progress...
                        </>
                      ) : (
                        'Start S3 Migration'
                      )}
                    </Button>
                    <Button 
                      variant="outline"
                      onClick={fetchMigrationStatus}
                      disabled={pollingMigration}
                    >
                      Refresh Status
                    </Button>
                  </div>

                  {/* Error Details */}
                  {migrationStatus?.error_details?.length > 0 && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <h4 className="font-semibold text-red-800 mb-2">Errors ({migrationStatus.error_details.length})</h4>
                      <div className="max-h-40 overflow-y-auto text-sm text-red-700">
                        {migrationStatus.error_details.slice(0, 10).map((err, i) => (
                          <p key={i}>• {err.photo_id}: {err.error}</p>
                        ))}
                        {migrationStatus.error_details.length > 10 && (
                          <p>... and {migrationStatus.error_details.length - 10} more</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </TabsContent>

              {/* Subscriptions Tab */}
              <TabsContent value="subscriptions">
                <div className="space-y-6">
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <h3 className="font-semibold text-yellow-800 mb-2">Payment Management</h3>
                    <p className="text-sm text-yellow-700">
                      Use this tool to audit Pro users and sync Stripe payment data. Users who are marked as "Pro" but haven't paid can be downgraded. 
                      Users who are marked as "Pro" but aren't actually paying can be downgraded.
                    </p>
                  </div>

                  {/* Load Pro Users Button */}
                  <Button 
                    onClick={fetchProUsers}
                    disabled={proUsersLoading}
                    variant="outline"
                  >
                    {proUsersLoading ? 'Loading...' : 'Load Pro Users'}
                  </Button>

                  {/* Pro Users Summary */}
                  {proUsers.length > 0 && (
                    <div className="bg-secondary/30 rounded-lg p-4">
                      <p className="text-lg font-semibold">
                        {proUsers.length} Pro Users = ${(proUsers.filter(u => u.subscription_status === 'active' || u.subscription_status === 'lifetime').length * 20).toFixed(2)} total revenue
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Active: {proUsers.filter(u => u.subscription_status === 'active').length} | 
                        Cancelled: {proUsers.filter(u => u.subscription_status === 'cancelled').length} |
                        Other: {proUsers.filter(u => !['active', 'cancelled'].includes(u.subscription_status)).length}
                      </p>
                    </div>
                  )}

                  {/* Pro Users Table */}
                  {proUsers.length > 0 && (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-border">
                            <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Name</th>
                            <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Email</th>
                            <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Status</th>
                            <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Stripe ID</th>
                            <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {proUsers.map((proUser) => (
                            <tr key={proUser.id} className="border-b border-border/50 hover:bg-secondary/20">
                              <td className="py-3 px-4">{proUser.name}</td>
                              <td className="py-3 px-4 text-sm">{proUser.email}</td>
                              <td className="py-3 px-4">
                                <Badge className={
                                  proUser.subscription_status === 'active' 
                                    ? 'bg-green-100 text-green-700' 
                                    : 'bg-gray-100 text-gray-700'
                                }>
                                  {proUser.subscription_status || 'unknown'}
                                </Badge>
                              </td>
                              <td className="py-3 px-4 text-sm text-muted-foreground">
                                {proUser.stripe_subscription_id 
                                  ? proUser.stripe_subscription_id.slice(0, 15) + '...' 
                                  : <span className="text-red-500">No Stripe ID</span>
                                }
                              </td>
                              <td className="py-3 px-4 space-x-2">
                                {!proUser.stripe_subscription_id && (
                                  <Button 
                                    size="sm" 
                                    variant="outline"
                                    onClick={() => handleSyncStripeIds(proUser.id, proUser.name)}
                                    disabled={syncingUserId === proUser.id}
                                  >
                                    {syncingUserId === proUser.id ? 'Syncing...' : 'Sync IDs'}
                                  </Button>
                                )}
                                <Button 
                                  size="sm" 
                                  variant="destructive"
                                  onClick={() => handleDowngradeUser(proUser.id, proUser.name)}
                                >
                                  Downgrade
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {proUsers.length === 0 && !proUsersLoading && (
                    <p className="text-muted-foreground text-center py-8">
                      Click "Load Pro Users" to view subscription details
                    </p>
                  )}
                </div>
              </TabsContent>
            </CardContent>
          </Tabs>
        </Card>
      </main>
    </div>
  );
};

export default Admin;
